import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ArticleCard from '@/components/base/ArticleCard';
import NewsletterForm from '@/components/base/NewsletterForm';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-600 text-lg mb-4">文章不存在</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.publishedAt,
          image: article.imageUrl,
          url: `https://snjrh.cn/article/${article.slug}`,
          publisher: {
            '@type': 'Organization',
            name: '亚洲人成长之路',
          },
          articleBody: article.content.replace(/<[^>]*>/g, '').substring(0, 500),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>

            <header className="py-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
                >
                  {article.category}
                </Link>
                <span className="text-sm text-foreground-500">{article.readTime} 分钟阅读</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-6">
                {article.title}
              </h1>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <span className="block text-sm font-medium text-foreground-900">
                    {article.author}
                  </span>
                  <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                </div>
              </div>
            </header>

            <div className="w-full h-72 md:h-96 rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose-custom pb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 py-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-14 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}

          <NewsletterForm />
        </main>

        <Footer />
        <ScrollToTop />
      </div>

      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.5rem;
          font-weight: 700;
          color: oklch(var(--foreground-900));
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          line-height: 1.35;
        }
        .prose-custom h3 {
          font-family: var(--font-heading);
          font-size: 1.2rem;
          font-weight: 600;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          line-height: 1.4;
        }
        .prose-custom p {
          font-size: 0.95rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 1.25rem;
        }
        .prose-custom ul, .prose-custom ol {
          margin-bottom: 1.25rem;
          padding-left: 1.5rem;
        }
        .prose-custom li {
          font-size: 0.95rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 0.5rem;
        }
        .prose-custom strong {
          color: oklch(var(--foreground-900));
          font-weight: 600;
        }
      `}</style>
    </>
  );
}